// src/components/AdminDashboard.js

import React, { useState, useEffect, useMemo } from "react";
import { db } from "../firebaseConfig";
import { collection, getDocs } from "firebase/firestore";

const USER_TYPE_LABELS = {
  migrante_refugiado: "Migrantes / Refugiados",
  representante_organizacao: "Representantes de organização",
  apoiador: "Apoiadores",
};

const CATEGORY_LABELS = {
  moradia: "Moradia",
  alimentacao: "Alimentação",
  trocas: "Trocas",
  transporte: "Transporte",
  documentacao: "Documentação",
  emprego: "Emprego",
  comunicacao: "Comunicação",
  treinamento: "Treinamento",
  saude: "Saúde",
  outros: "Outros",
};

const PALETTE = [
  "#5E205D",
  "#D13C8D",
  "#EE7913",
  "#4663AB",
  "#2DA7A6",
  "#9B59B6",
  "#F1C40F",
  "#27AE60",
  "#E74C3C",
  "#7F8C8D",
];

const toMillis = (value) => {
  if (!value) return null;
  if (value.toMillis) return value.toMillis();
  if (value.seconds) return value.seconds * 1000;
  const parsed = new Date(value);
  return isNaN(parsed.getTime()) ? null : parsed.getTime();
};

const getMuralStatus = (item) => {
  if (item.archivedAt) return "arquivado";
  if (item.expiresAt) {
    const ms = toMillis(item.expiresAt);
    if (ms !== null && ms < Date.now()) return "expirado";
  }
  if (item.isVisible === false) return "oculto";
  return "ativo";
};

const addressFieldOf = (record, key) => {
  if (
    record.address &&
    typeof record.address === "object" &&
    record.address[key]
  ) {
    return record.address[key];
  }
  return record[key] || "";
};

const Card = ({ label, value, hint, accent }) => (
  <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
    <p className="text-xs uppercase text-gray-500 font-bold tracking-wide">
      {label}
    </p>
    <p
      className="text-3xl font-bold mt-1"
      style={{ color: accent || "#5E205D" }}
    >
      {value}
    </p>
    {hint && <p className="text-xs text-gray-500 mt-1">{hint}</p>}
  </div>
);

const BarRow = ({ label, value, max, color }) => {
  const pct = max > 0 ? Math.max(2, (value / max) * 100) : 0;
  return (
    <div className="flex items-center gap-2 text-sm mb-1">
      <span className="w-44 text-gray-700 truncate" title={label}>
        {label}
      </span>
      <div className="flex-1 bg-gray-100 rounded h-3 overflow-hidden">
        <div className="h-3" style={{ width: `${pct}%`, background: color }} />
      </div>
      <span className="w-10 text-right text-gray-700 tabular-nums">
        {value}
      </span>
    </div>
  );
};

const Donut = ({ data, size = 140 }) => {
  const total = data.reduce((acc, d) => acc + d.value, 0);
  if (total === 0) {
    return <p className="text-xs text-gray-400">Sem dados</p>;
  }
  const radius = size / 2;
  const stroke = 18;
  const r = radius - stroke / 2;
  const circumference = 2 * Math.PI * r;
  let offset = 0;
  return (
    <div className="flex items-center gap-4">
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle
          cx={radius}
          cy={radius}
          r={r}
          fill="none"
          stroke="#F1F1F1"
          strokeWidth={stroke}
        />
        {data.map((d, idx) => {
          const fraction = d.value / total;
          const dash = circumference * fraction;
          const gap = circumference - dash;
          const segment = (
            <circle
              key={d.key}
              cx={radius}
              cy={radius}
              r={r}
              fill="none"
              stroke={d.color || PALETTE[idx % PALETTE.length]}
              strokeWidth={stroke}
              strokeDasharray={`${dash} ${gap}`}
              strokeDashoffset={-offset}
              transform={`rotate(-90 ${radius} ${radius})`}
            />
          );
          offset += dash;
          return segment;
        })}
        <text
          x="50%"
          y="50%"
          textAnchor="middle"
          dy="0.35em"
          className="fill-gray-700"
          style={{ fontSize: 18, fontWeight: 700 }}
        >
          {total}
        </text>
      </svg>
      <div>
        {data.map((d, idx) => (
          <div key={d.key} className="flex items-center text-xs mb-1">
            <span
              className="inline-block w-3 h-3 rounded-sm mr-2"
              style={{ background: d.color || PALETTE[idx % PALETTE.length] }}
            />
            <span className="text-gray-700">
              {d.label}: <strong>{d.value}</strong>
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

const AdminDashboard = () => {
  const [users, setUsers] = useState([]);
  const [muralItems, setMuralItems] = useState([]);
  const [entrepreneurs, setEntrepreneurs] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [muralEvaluations, setMuralEvaluations] = useState([]);
  const [loading, setLoading] = useState(true);

  const [periodStart, setPeriodStart] = useState("");
  const [periodEnd, setPeriodEnd] = useState("");
  const [stateFilter, setStateFilter] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");

  useEffect(() => {
    const fetchAll = async () => {
      // Busca cada coleção isoladamente: se uma falhar (ex.: regra do Firestore),
      // as demais ainda carregam, e o erro específico fica visível no console.
      const loadCollection = async (name, setter) => {
        try {
          const snap = await getDocs(collection(db, name));
          setter(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
          console.log(`[Dashboard] '${name}': ${snap.size} documento(s).`);
        } catch (err) {
          console.error(`[Dashboard] Falha ao ler '${name}':`, err.code || err.message, err);
        }
      };

      await Promise.allSettled([
        loadCollection("users", setUsers),
        loadCollection("mural", setMuralItems),
        loadCollection("entrepreneurs", setEntrepreneurs),
        loadCollection("organizations", setOrganizations),
        loadCollection("muralEvaluations", setMuralEvaluations),
      ]);
      setLoading(false);
    };
    fetchAll();
  }, []);

  const periodFilter = useMemo(() => {
    const startMs = periodStart
      ? new Date(`${periodStart}T00:00:00`).getTime()
      : null;
    const endMs = periodEnd
      ? new Date(`${periodEnd}T23:59:59`).getTime()
      : null;
    return (createdAt) => {
      if (startMs === null && endMs === null) return true;
      const ms = toMillis(createdAt);
      if (ms === null) return startMs === null;
      if (startMs !== null && ms < startMs) return false;
      if (endMs !== null && ms > endMs) return false;
      return true;
    };
  }, [periodStart, periodEnd]);

  const filteredUsers = useMemo(
    () =>
      users.filter((u) => {
        if (!periodFilter(u.createdAt)) return false;
        if (stateFilter && addressFieldOf(u, "state") !== stateFilter)
          return false;
        return true;
      }),
    [users, periodFilter, stateFilter],
  );

  const filteredMural = useMemo(
    () =>
      muralItems.filter((it) => {
        if (!periodFilter(it.createdAt)) return false;
        if (stateFilter && it.estado !== stateFilter) return false;
        if (categoryFilter && it.category !== categoryFilter) return false;
        return true;
      }),
    [muralItems, periodFilter, stateFilter, categoryFilter],
  );

  const filteredEntrepreneurs = useMemo(
    () =>
      entrepreneurs.filter((e) => {
        if (!periodFilter(e.createdAt)) return false;
        if (stateFilter && e.businessState !== stateFilter) return false;
        if (categoryFilter) {
          const cats =
            Array.isArray(e.categories) && e.categories.length > 0
              ? e.categories
              : e.category
                ? [e.category]
                : [];
          if (!cats.includes(categoryFilter)) return false;
        }
        return true;
      }),
    [entrepreneurs, periodFilter, stateFilter, categoryFilter],
  );

  const usersByType = useMemo(() => {
    const counts = {
      migrante_refugiado: 0,
      representante_organizacao: 0,
      apoiador: 0,
      indefinido: 0,
    };
    filteredUsers.forEach((u) => {
      counts[u.userType || "indefinido"] =
        (counts[u.userType || "indefinido"] || 0) + 1;
    });
    return counts;
  }, [filteredUsers]);

  const muralByStatus = useMemo(() => {
    const counts = { ativo: 0, oculto: 0, arquivado: 0, expirado: 0 };
    filteredMural.forEach((it) => {
      const status = getMuralStatus(it);
      counts[status] = (counts[status] || 0) + 1;
    });
    return counts;
  }, [filteredMural]);

  const muralByType = useMemo(() => {
    const counts = { offer: 0, demand: 0 };
    filteredMural.forEach((it) => {
      if (it.type === "offer") counts.offer++;
      else if (it.type === "demand") counts.demand++;
    });
    return counts;
  }, [filteredMural]);

  const muralByCategory = useMemo(() => {
    const counts = {};
    filteredMural.forEach((it) => {
      const key = it.category || "outros";
      counts[key] = (counts[key] || 0) + 1;
    });
    return counts;
  }, [filteredMural]);

  const usersByState = useMemo(() => {
    const counts = {};
    filteredUsers.forEach((u) => {
      const st = addressFieldOf(u, "state") || "—";
      counts[st] = (counts[st] || 0) + 1;
    });
    return counts;
  }, [filteredUsers]);

  const evaluationStats = useMemo(() => {
    const total = muralEvaluations.length;
    const ratios = { fulfilled: 0, viaPlatform: 0, recommend: 0 };
    muralEvaluations.forEach((ev) => {
      const a = ev.answers || {};
      if (a.fulfilled) ratios.fulfilled++;
      if (a.viaPlatform) ratios.viaPlatform++;
      if (a.recommend) ratios.recommend++;
    });
    return { total, ratios };
  }, [muralEvaluations]);

  const totalActiveOffers = muralByStatus.ativo;
  const totalArchived = muralByStatus.arquivado;
  const totalExpired = muralByStatus.expirado;

  const maxStateCount = Math.max(0, ...Object.values(usersByState));
  const maxCategoryCount = Math.max(0, ...Object.values(muralByCategory));

  if (loading) {
    return <div className="p-4 text-gray-600">Carregando indicadores...</div>;
  }

  return (
    <div className="p-2 md:p-4">
      <h1 className="text-2xl font-bold text-purple-refugia mb-4">Dashboard</h1>

      <div className="bg-white p-3 rounded-xl border border-gray-200 mb-5 flex flex-wrap items-end gap-3">
        <label className="text-sm text-gray-700">
          De:&nbsp;
          <input
            type="date"
            value={periodStart}
            onChange={(e) => setPeriodStart(e.target.value)}
            className="border border-gray-300 rounded px-2 py-1"
          />
        </label>
        <label className="text-sm text-gray-700">
          Até:&nbsp;
          <input
            type="date"
            value={periodEnd}
            onChange={(e) => setPeriodEnd(e.target.value)}
            className="border border-gray-300 rounded px-2 py-1"
          />
        </label>
        <label className="text-sm text-gray-700">
          Estado (UF):&nbsp;
          <input
            type="text"
            value={stateFilter}
            onChange={(e) =>
              setStateFilter(e.target.value.toUpperCase().slice(0, 2))
            }
            placeholder="Ex: SP"
            className="border border-gray-300 rounded px-2 py-1 w-20"
          />
        </label>
        <label className="text-sm text-gray-700">
          Categoria:&nbsp;
          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            className="border border-gray-300 rounded px-2 py-1"
          >
            <option value="">Todas</option>
            {Object.entries(CATEGORY_LABELS).map(([key, label]) => (
              <option key={key} value={key}>
                {label}
              </option>
            ))}
          </select>
        </label>
        {(periodStart || periodEnd || stateFilter || categoryFilter) && (
          <button
            type="button"
            onClick={() => {
              setPeriodStart("");
              setPeriodEnd("");
              setStateFilter("");
              setCategoryFilter("");
            }}
            className="text-sm text-pink-600 hover:underline"
          >
            Limpar filtros
          </button>
        )}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <Card
          label="Migrantes/Refugiados"
          value={usersByType.migrante_refugiado}
        />
        <Card
          label="Representantes de organização"
          value={usersByType.representante_organizacao}
        />
        <Card label="Apoiadores" value={usersByType.apoiador} />
        <Card
          label="Organizações"
          value={organizations.length}
          hint="Distintas por CNPJ"
        />
        <Card
          label="Empreendedores"
          value={filteredEntrepreneurs.length}
          accent="#EE7913"
        />
        <Card
          label="Ofertas/Demandas ativas"
          value={totalActiveOffers}
          accent="#27AE60"
        />
        <Card label="Arquivadas" value={totalArchived} accent="#7F8C8D" />
        <Card label="Expiradas" value={totalExpired} accent="#E74C3C" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <div className="bg-white rounded-xl p-4 border border-gray-200">
          <h2 className="font-bold text-purple-refugia mb-3">
            Usuários por tipo
          </h2>
          <Donut
            data={[
              {
                key: "m",
                label: USER_TYPE_LABELS.migrante_refugiado,
                value: usersByType.migrante_refugiado,
              },
              {
                key: "r",
                label: USER_TYPE_LABELS.representante_organizacao,
                value: usersByType.representante_organizacao,
              },
              {
                key: "a",
                label: USER_TYPE_LABELS.apoiador,
                value: usersByType.apoiador,
              },
              { key: "i", label: "Indefinido", value: usersByType.indefinido },
            ]}
          />
        </div>
        <div className="bg-white rounded-xl p-4 border border-gray-200">
          <h2 className="font-bold text-purple-refugia mb-3">
            Mural — ofertas vs demandas
          </h2>
          <Donut
            data={[
              {
                key: "o",
                label: "Ofertas",
                value: muralByType.offer,
                color: "#27AE60",
              },
              {
                key: "d",
                label: "Demandas",
                value: muralByType.demand,
                color: "#D13C8D",
              },
            ]}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <div className="bg-white rounded-xl p-4 border border-gray-200">
          <h2 className="font-bold text-purple-refugia mb-3">
            Mural por categoria
          </h2>
          {Object.entries(CATEGORY_LABELS).map(([key, label], idx) => (
            <BarRow
              key={key}
              label={label}
              value={muralByCategory[key] || 0}
              max={maxCategoryCount}
              color={PALETTE[idx % PALETTE.length]}
            />
          ))}
        </div>
        <div className="bg-white rounded-xl p-4 border border-gray-200">
          <h2 className="font-bold text-purple-refugia mb-3">
            Usuários por estado
          </h2>
          {Object.entries(usersByState)
            .sort((a, b) => b[1] - a[1])
            .slice(0, 12)
            .map(([state, value], idx) => (
              <BarRow
                key={state}
                label={state || "—"}
                value={value}
                max={maxStateCount}
                color={PALETTE[idx % PALETTE.length]}
              />
            ))}
        </div>
      </div>

      <div className="bg-white rounded-xl p-4 border border-gray-200">
        <h2 className="font-bold text-purple-refugia mb-3">
          Avaliações finais (mural)
        </h2>
        <p className="text-sm text-gray-600 mb-2">
          Baseadas em <strong>{evaluationStats.total}</strong> avaliações
          registradas.
        </p>
        {evaluationStats.total > 0 ? (
          <>
            <BarRow
              label="Oferta/demanda atendida"
              value={evaluationStats.ratios.fulfilled}
              max={evaluationStats.total}
              color="#27AE60"
            />
            <BarRow
              label="Atendida pela plataforma"
              value={evaluationStats.ratios.viaPlatform}
              max={evaluationStats.total}
              color="#4663AB"
            />
            <BarRow
              label="Recomendaria a plataforma"
              value={evaluationStats.ratios.recommend}
              max={evaluationStats.total}
              color="#EE7913"
            />
          </>
        ) : (
          <p className="text-sm text-gray-500">
            Ainda não há avaliações registradas.
          </p>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
